const TEAM = [
  { name: "Dr. Chibuzo Okonkwo", role: "Executive Director & Founder", bio: "A development economist with 18 years experience in community finance and social impact programs across Sub-Saharan Africa.", initials: "CO" },
  { name: "Fatima Al-Rasheed", role: "Director of Programs", bio: "Former UNICEF program officer with expertise in education and youth empowerment initiatives across Northern Nigeria.", initials: "FA" },
  { name: "James Adeyemi", role: "Chief Financial Officer", bio: "Certified public accountant and nonprofit finance specialist ensuring full transparency in all grant disbursements.", initials: "JA" },
  { name: "Dr. Blessing Nwosu", role: "Community Relations Lead", bio: "Sociologist and community organizer who has worked with over 200 NGOs to build capacity and secure sustainable funding.", initials: "BN" },
];

const VALUES = [
  { icon: "🔍", title: "Transparency", desc: "Every naira distributed is publicly tracked and reported to our member community." },
  { icon: "⚖️", title: "Equity", desc: "We prioritize underserved communities and ensure fair access regardless of geography or connections." },
  { icon: "🌿", title: "Sustainability", desc: "We fund projects designed to create lasting change, not temporary relief." },
  { icon: "🤝", title: "Partnership", desc: "We work alongside grantees — not just as funders, but as long-term collaborators." },
];

export default function AboutPage() {
  return (
    <>
      <style>{`
        .about-hero { background: linear-gradient(160deg,#071428,#0B1F3A,#122844); padding:140px 5vw 80px; text-align:center; }
        .about-hero h1 { font-family:'Playfair Display',serif; font-size:clamp(38px,6vw,72px); font-weight:900; color:white; line-height:1.1; margin-bottom:20px; }
        .story-grid { display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:center; max-width:1200px; margin:0 auto; }
        .story-text p { color:var(--text-muted); font-size:16px; line-height:1.85; margin-bottom:18px; }
        .story-visual { background:linear-gradient(135deg,#0B1F3A,#1a3a5c); border-radius:4px; padding:40px 36px; }
        .tl-item { display:flex; gap:14px; align-items:flex-start; margin-bottom:6px; }
        .tl-year { font-family:'Playfair Display',serif; font-size:18px; font-weight:700; color:#E8B96A; min-width:48px; }
        .tl-desc { color:rgba(255,255,255,0.65); font-size:13px; line-height:1.6; padding-top:2px; }
        .tl-line { width:1px; background:rgba(201,150,62,0.15); margin:4px 0 4px 24px; height:10px; }
        .values-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px; max-width:1200px; margin:48px auto 0; }
        .value-card { padding:32px 24px; border:1px solid var(--border-light); background:white; }
        .team-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:28px; max-width:1200px; margin:52px auto 0; }
        .team-card { text-align:center; padding:36px 24px; border:1px solid var(--border-light); background:white; transition:all 0.3s; }
        .team-card:hover { transform:translateY(-4px); box-shadow:var(--shadow-md); }
        .team-avatar { width:80px; height:80px; border-radius:50%; margin:0 auto 16px; background:linear-gradient(135deg,#0B1F3A,#1a3a5c); display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:22px; font-weight:700; color:#E8B96A; }
        @media(max-width:900px){ .story-grid{grid-template-columns:1fr;} .values-grid{grid-template-columns:repeat(2,1fr);} .team-grid{grid-template-columns:repeat(2,1fr);} }
        @media(max-width:560px){ .values-grid,.team-grid{grid-template-columns:1fr;} }
      `}</style>

      <section className="about-hero">
        <span className="section-eyebrow" style={{ color: "#E8B96A" }}>About Us</span>
        <h1>Who We Are &<br /><em style={{ fontStyle: "italic", color: "#E8B96A" }}>Why We Exist</em></h1>
        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 17, maxWidth: 580, margin: "0 auto", lineHeight: 1.75 }}>
          A foundation built on conviction — that every community deserves access to the resources it needs to change its own story.
        </p>
      </section>

      {/* STORY */}
      <section style={{ padding: "90px 5vw", background: "var(--off-white)" }}>
        <div className="story-grid">
          <div className="story-text">
            <span className="section-eyebrow">Our Story</span>
            <h2 className="section-title">From a Single Idea to National Impact</h2>
            <p>
              Revolutionary Foundation was established in 2019 by a group of development practitioners, community leaders, and financial experts who saw a persistent gap: well-intentioned NGOs and grassroots projects with immense potential but no access to sustainable funding.
            </p>
            <p>
              We began with 42 founding members and a ₦1.5 million seed fund. Today with 18,400+ members and ₦4.5 Billion distributed across 27 projects, we are Nigeria's fastest-growing grant foundation.
            </p>
            <p>
              We also act as a professional intermediary between international philanthropists, government agencies, and local Nigerian communities — ensuring donor funds reach vetted beneficiaries with full transparency.
            </p>
          </div>
          <div className="story-visual">
            <div style={{ fontSize: 11, color: "#E8B96A", letterSpacing: 2, textTransform: "uppercase", marginBottom: 20 }}>Foundation Timeline</div>
            {[
              ["2019", "Founded with 42 members and ₦1.5m seed fund"],
              ["2020", "Funded first 12 community projects across 5 states"],
              ["2021", "Membership grew to 3,000. Digital platform launched"],
              ["2022", "Partnered with 6 state governments on education grants"],
              ["2023", "Surpassed ₦2 Billion in total distributions"],
              ["2024", "Expanded to all 36 states"],
              ["2025", "18,400+ members, ₦4.5 Billion total distribution across 27 projects funded"],
            ].map(([year, desc], i) => (
              <div key={year}>
                <div className="tl-item">
                  <div className="tl-year">{year}</div>
                  <div className="tl-desc">{desc}</div>
                </div>
                {i < 6 && <div className="tl-line" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section style={{ padding: "80px 5vw", background: "white" }}>
        <div style={{ textAlign: "center" }}>
          <span className="section-eyebrow">What Guides Us</span>
          <h2 className="section-title">Our Core Values</h2>
        </div>
        <div className="values-grid">
          {VALUES.map(v => (
            <div className="value-card" key={v.title}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{v.icon}</div>
              <div className="gold-line" />
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, color: "var(--navy)", marginBottom: 10 }}>{v.title}</div>
              <div style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.7 }}>{v.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TEAM */}
      <section id="team" style={{ padding: "80px 5vw", background: "var(--off-white)" }}>
        <div style={{ textAlign: "center" }}>
          <span className="section-eyebrow">Leadership</span>
          <h2 className="section-title">Meet the Team</h2>
        </div>
        <div className="team-grid">
          {TEAM.map(t => (
            <div className="team-card" key={t.name}>
              <div className="team-avatar">{t.initials}</div>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700, color: "var(--navy)", marginBottom: 4 }}>{t.name}</div>
              <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 600, letterSpacing: 0.5, marginBottom: 12, textTransform: "uppercase" }}>{t.role}</div>
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.65 }}>{t.bio}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HQ INFO */}
      <section style={{ background: "#0B1F3A", padding: "60px 5vw" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 48, maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
          {[["🌍","Operational Reach","Active across all 36 States"],["📅","Established","Since 2019"],["🛡️","Accountability","100% Transparency in Disbursement"]].map(([icon,label,val]) => (
            <div key={label}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{icon}</div>
              <div style={{ fontSize: 11, letterSpacing: 2, textTransform: "uppercase", color: "#E8B96A", marginBottom: 6 }}>{label}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 15 }}>{val}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
